import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { immobiliApi, usersApi, documentiImmobileApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import { ComboboxMulti } from '../../components/ui/combobox';
import { Plus, Search, Edit2, Trash2, Home, MapPin, Users, Lock, FileText, FileSpreadsheet, Archive, CheckCircle2, Clock, ArrowUpDown, ArrowUp, ArrowDown, Upload, Download, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ImmobiliPage = () => {
  const [immobili, setImmobili] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStato, setFilterStato] = useState('in_gestione');
  const [showDialog, setShowDialog] = useState(false);
  const [showEditProprietariDialog, setShowEditProprietariDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedProprietariIds, setSelectedProprietariIds] = useState([]);
  const [deleteChecks, setDeleteChecks] = useState({}); // Cache can-delete checks

  // Documenti immobile
  const [showDocDialog, setShowDocDialog] = useState(false);
  const [docImmobile, setDocImmobile] = useState(null);
  const [documentiImmobile, setDocumentiImmobile] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docTipo, setDocTipo] = useState('');
  const [docNote, setDocNote] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const tipiDocImmobile = [
    { value: 'provenienza', label: 'Atto di Provenienza' },
    { value: 'visura_catastale', label: 'Visura Catastale' },
    { value: 'planimetria_catastale', label: 'Planimetria Catastale' },
    { value: 'attestato_energetico', label: 'Attestato Energetico (APE)' },
    { value: 'regolamento_condominio', label: 'Regolamento di Condominio' },
    { value: 'dico_impianto_elettrico', label: 'DICO Impianto Elettrico' },
    { value: 'dico_impianto_idraulico', label: 'DICO Impianto Idraulico' },
    { value: 'dico_impianto_riscaldamento', label: 'DICO Impianto Riscaldamento' },
    { value: 'altro', label: 'Altro' },
  ];
  
  // Ordinamento colonne
  const [sortColumn, setSortColumn] = useState('citta');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Ricerca comuni
  const [comuniSuggestions, setComuniSuggestions] = useState([]);
  const [searchingComuni, setSearchingComuni] = useState(false);
  
  const [showNewProprietarioDialog, setShowNewProprietarioDialog] = useState(false);
  const [newProprietarioData, setNewProprietarioData] = useState({
    nome: '',
    cognome: '',
    email: '',
    codice_fiscale: '',
    iban: '',
    is_societa: false,
    ragione_sociale: ''
  });
  const [formData, setFormData] = useState({
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    proprietari_ids: [],
    stato: 'disponibile',
    codice_comune: '',
    foglio: '',
    particella: '',
    subalterno: '',
    categoria: '',
    classe: '',
    consistenza: '',
    superficie_catastale: '',
    rendita: '',
    note: ''
  });

  const checkDeleteEligibility = useCallback(async (properties) => {
    const checks = {};
    // Load in parallel batches of 5
    for (let i = 0; i < properties.length; i += 5) {
      const batch = properties.slice(i, i + 5);
      const results = await Promise.all(
        batch.map(async (imm) => {
          try {
            const res = await axios.get(`${API_URL}/properties/${imm.id}/can-delete`);
            return { id: imm.id, data: res.data };
          } catch {
            return { id: imm.id, data: { can_delete: true } };
          }
        })
      );
      results.forEach(r => { checks[r.id] = r.data; });
      setDeleteChecks(prev => ({ ...prev, ...checks }));
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const params = filterStato && filterStato !== 'all' ? `?stato=${filterStato}` : '';
      const [immRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/properties${params}`),
        usersApi.getAll()
      ]);
      setImmobili(immRes.data);
      setAllUsers(usersRes.data.filter(u => u.ruolo !== 'admin'));
      
      // Check delete eligibility for each property in batch (async but don't wait)
      checkDeleteEligibility(immRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [filterStato, checkDeleteEligibility]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Ricerca comuni per città
  const searchComuni = async (query) => {
    if (!query || query.length < 2) {
      setComuniSuggestions([]);
      return;
    }
    setSearchingComuni(true);
    try {
      const res = await axios.get(`${API_URL}/comuni?q=${encodeURIComponent(query)}`);
      setComuniSuggestions(res.data || []);
    } catch (err) {
      console.error('Errore ricerca comuni:', err);
    } finally {
      setSearchingComuni(false);
    }
  };

  // Seleziona comune
  const selectComune = (comune) => {
    const capList = comune.cap_list || [];
    const capValue = capList.length === 1 ? capList[0] : '';
    
    setFormData({
      ...formData,
      citta: comune.nome,
      provincia: comune.sigla_provincia || comune.provincia,
      cap: capValue,
      codice_comune: comune.codice_catastale || ''
    });
    setComuniSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.proprietari_ids.length === 0) {
      toast.error('Seleziona almeno un proprietario');
      return;
    }

    try {
      if (editingItem) {
        // In edit mode, only update non-proprietari fields
        const { proprietari_ids, ...updateData } = formData;
        await immobiliApi.update(editingItem.id, updateData);
        toast.success('Immobile aggiornato');
      } else {
        await immobiliApi.create(formData);
        toast.success('Immobile creato');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    // Handle both old (proprietario_id) and new (proprietari_ids) format
    const propIds = item.proprietari_ids || (item.proprietario_id ? [item.proprietario_id] : []);
    setFormData({
      indirizzo: item.indirizzo,
      citta: item.citta,
      cap: item.cap,
      provincia: item.provincia,
      proprietari_ids: propIds,
      stato: item.stato || 'disponibile',
      codice_comune: item.codice_comune || '',
      foglio: item.foglio || '',
      particella: item.particella || '',
      subalterno: item.subalterno || '',
      categoria: item.categoria || '',
      classe: item.classe || '',
      consistenza: item.consistenza || '',
      superficie_catastale: item.superficie_catastale || '',
      rendita: item.rendita || '',
      note: item.note || ''
    });
    setShowDialog(true);
  };

  const handleEditProprietari = (item) => {
    setEditingItem(item);
    const propIds = item.proprietari_ids || (item.proprietario_id ? [item.proprietario_id] : []);
    setSelectedProprietariIds(propIds);
    setShowEditProprietariDialog(true);
  };

  const handleSaveProprietari = async () => {
    if (selectedProprietariIds.length === 0) {
      toast.error('Seleziona almeno un proprietario');
      return;
    }

    try {
      await immobiliApi.update(editingItem.id, { proprietari_ids: selectedProprietariIds });
      toast.success('Proprietari aggiornati');
      setShowEditProprietariDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (id) => {
    const check = deleteChecks[id];
    if (check && !check.can_delete) {
      toast.error(`Impossibile eliminare: ${check.reason || 'Immobile associato a contratti'}`);
      return;
    }
    
    if (window.confirm('Eliminare questo immobile?')) {
      try {
        await immobiliApi.delete(id);
        toast.success('Immobile eliminato');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
      }
    }
  };

  const handleArchive = async (id) => {
    if (window.confirm('Archiviare questo immobile? Non sarà più tra i disponibili (nessun contratto in gestione).')) {
      try {
        await immobiliApi.update(id, { stato: 'archiviato' });
        toast.success('Immobile archiviato');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Errore nell\'archiviazione');
      }
    }
  };

  // Export functions
  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API_URL}/export/properties/${format}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `immobili.${format === 'xlsx' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export ${format.toUpperCase()} completato`);
    } catch (error) {
      toast.error('Errore durante l\'export');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      indirizzo: '',
      citta: '',
      cap: '',
      provincia: '',
      proprietari_ids: [],
      stato: 'disponibile',
      codice_comune: '',
      foglio: '',
      particella: '',
      subalterno: '',
      categoria: '',
      classe: '',
      consistenza: '',
      superficie_catastale: '',
      rendita: '',
      note: ''
    });
  };

  // Crea nuovo proprietario inline
  const handleCreateProprietario = async () => {
    try {
      if (!newProprietarioData.is_societa && (!newProprietarioData.nome || !newProprietarioData.cognome)) {
        toast.error('Nome e Cognome sono obbligatori');
        return;
      }
      if (newProprietarioData.is_societa && !newProprietarioData.ragione_sociale) {
        toast.error('Ragione Sociale è obbligatoria per le società');
        return;
      }

      const response = await usersApi.create(newProprietarioData);
      const newUser = response.data;
      
      // Aggiorna lista utenti
      setAllUsers([...allUsers, newUser]);
      
      // Seleziona automaticamente il nuovo proprietario
      setFormData({...formData, proprietari_ids: [...formData.proprietari_ids, newUser.id]});
      
      // Reset e chiudi dialog
      setNewProprietarioData({
        nome: '', cognome: '', email: '', codice_fiscale: '', iban: '',
        is_societa: false, ragione_sociale: ''
      });
      setShowNewProprietarioDialog(false);
      
      toast.success(`Proprietario "${newProprietarioData.is_societa ? newProprietarioData.ragione_sociale : `${newProprietarioData.nome} ${newProprietarioData.cognome}`}" creato e selezionato`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  // Helper per nomi proprietari (Cognome Nome o Ragione Sociale)
  const getProprietariNomi = (item) => {
    const propIds = item.proprietari_ids || (item.proprietario_id ? [item.proprietario_id] : []);
    return propIds.map(id => {
      const prop = allUsers.find(p => p.id === id);
      if (!prop) return 'N/D';
      if (prop.is_societa && prop.ragione_sociale) return prop.ragione_sociale;
      return `${prop.cognome || ''} ${prop.nome || ''}`.trim() || 'N/D';
    }).join(', ');
  };

  const filteredImmobili = immobili.filter(imm => {
    const propNomi = getProprietariNomi(imm).toLowerCase();
    return imm.indirizzo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      imm.citta.toLowerCase().includes(searchTerm.toLowerCase()) ||
      propNomi.includes(searchTerm.toLowerCase());
  });

  // Ordinamento colonne
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-40" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" /> 
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  // Ordinamento effettivo
  const sortedImmobili = useMemo(() => {
    return [...filteredImmobili].sort((a, b) => {
      let valA, valB;
      
      switch (sortColumn) {
        case 'citta':
          valA = a.citta || '';
          valB = b.citta || '';
          break;
        case 'indirizzo':
          valA = a.indirizzo || '';
          valB = b.indirizzo || '';
          break;
        case 'proprietario':
          valA = getProprietariNomi(a);
          valB = getProprietariNomi(b);
          break;
        case 'stato':
          valA = a.stato || '';
          valB = b.stato || '';
          break;
        default:
          valA = a[sortColumn] || '';
          valB = b[sortColumn] || '';
      }
      
      const comparison = String(valA).localeCompare(String(valB));
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredImmobili, sortColumn, sortDirection]);

  const getStatoBadge = (stato) => {
    const badges = {
      disponibile: { className: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Disponibile' },
      in_gestione: { className: 'bg-blue-100 text-blue-700', icon: Clock, label: 'In Gestione' },
      archiviato: { className: 'bg-slate-100 text-slate-600', icon: Archive, label: 'Archiviato' }
    };
    const badge = badges[stato] || badges.disponibile;
    return (
      <Badge className={`${badge.className} flex items-center gap-1`}>
        <badge.icon className="w-3 h-3" />
        {badge.label}
      </Badge>
    );
  };

  // Options for combobox
  const userOptions = allUsers.map(u => ({
    value: u.id,
    label: u.is_societa && u.ragione_sociale 
      ? u.ragione_sociale 
      : `${u.cognome || ''} ${u.nome || ''}`.trim(),
    sublabel: u.iban ? `IBAN: ${u.iban.substring(0, 12)}...` : 'IBAN non presente'
  }));

  if (loading) return <div className="p-6">Caricamento...</div>;

  const handleOpenDocumenti = async (imm) => {
    setDocImmobile(imm);
    setShowDocDialog(true);
    setLoadingDocs(true);
    try {
      const { data } = await documentiImmobileApi.getAll(imm.id);
      setDocumentiImmobile(data);
    } catch {
      toast.error('Errore caricamento documenti');
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleUploadDocImmobile = async () => {
    if (!docFile || !docTipo) {
      toast.error('Seleziona un file e un tipo documento');
      return;
    }
    setUploadingDoc(true);
    try {
      const ext = docFile.name.includes('.') ? '.' + docFile.name.split('.').pop().toLowerCase() : '';
      const tipoLabel = tipiDocImmobile.find(t => t.value === docTipo)?.label || docTipo;
      const immId = `${docImmobile?.indirizzo || ''} ${docImmobile?.civico || ''}`.trim();
      const renamedFilename = `${tipoLabel}${immId ? '_' + immId : ''}${ext}`.replace(/\s+/g, '_').replace(/\//g, '-');
      const formData = new FormData();
      formData.append('file', docFile, renamedFilename);
      formData.append('tipo_documento', docTipo);
      formData.append('note', docNote);
      await documentiImmobileApi.upload(docImmobile.id, formData);
      toast.success('Documento caricato');
      setDocFile(null);
      setDocTipo('');
      setDocNote('');
      setFileInputKey(prev => prev + 1);
      const { data } = await documentiImmobileApi.getAll(docImmobile.id);
      setDocumentiImmobile(data);
    } catch {
      toast.error('Errore durante il caricamento');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDownloadDocImmobile = async (doc) => {
    try {
      const response = await documentiImmobileApi.download(docImmobile.id, doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Errore download');
    }
  };

  const handleDeleteDocImmobile = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await documentiImmobileApi.delete(docImmobile.id, docId);
      toast.success('Documento eliminato');
      const { data } = await documentiImmobileApi.getAll(docImmobile.id);
      setDocumentiImmobile(data);
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="immobili-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Immobili</h1>
          <p className="text-sm text-slate-600">{immobili.length} immobili totali</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExport('xlsx')} data-testid="export-xlsx-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button variant="outline" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="new-immobile-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Immobile
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="mb-4 flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cerca per indirizzo o città..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-immobili"
              />
            </div>
            <Select value={filterStato} onValueChange={setFilterStato}>
              <SelectTrigger className="w-48" data-testid="filter-stato">
                <SelectValue placeholder="Filtra per stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="disponibile">Disponibile</SelectItem>
                <SelectItem value="in_gestione">In Gestione</SelectItem>
                <SelectItem value="archiviato">Archiviato</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="max-h-[70vh] overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-white z-10 shadow-sm">
              <TableRow>
                <TableHead className="w-12 text-center">#</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-50 select-none"
                  onClick={() => handleSort('citta')}
                >
                  <div className="flex items-center">Città {getSortIcon('citta')}</div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-50 select-none"
                  onClick={() => handleSort('indirizzo')}
                >
                  <div className="flex items-center">Indirizzo {getSortIcon('indirizzo')}</div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-50 select-none"
                  onClick={() => handleSort('proprietario')}
                >
                  <div className="flex items-center">Proprietari {getSortIcon('proprietario')}</div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-slate-50 select-none"
                  onClick={() => handleSort('stato')}
                >
                  <div className="flex items-center">Stato {getSortIcon('stato')}</div>
                </TableHead>
                <TableHead>Dati Catastali</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedImmobili.map((imm, index) => {
                const canDelete = deleteChecks[imm.id]?.can_delete;
                return (
                <TableRow key={imm.id} data-testid={`immobile-row-${imm.id}`}>
                  <TableCell className="text-center text-slate-400 font-mono text-sm">
                    {index + 1}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-slate-400" />
                      <span className="font-medium">{imm.citta}</span> ({imm.provincia})
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Home className="w-4 h-4 text-slate-400" />
                      {imm.indirizzo}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-sm">{getProprietariNomi(imm)}</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleEditProprietari(imm)}
                        className="ml-1 h-6 w-6 p-0"
                        title="Modifica Proprietari"
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getStatoBadge(imm.stato || 'disponibile')}
                  </TableCell>
                  <TableCell>
                    {imm.foglio || imm.codice_comune ? (
                      <span className="text-sm font-mono">
                        {[imm.codice_comune, imm.foglio, imm.particella, imm.subalterno].filter(Boolean).join('.')}
                      </span>
                    ) : (
                      <span className="text-slate-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDocumenti(imm)} data-testid={`docs-imm-${imm.id}`} title="Documenti">
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(imm)} data-testid={`edit-imm-${imm.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {imm.stato === 'disponibile' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleArchive(imm.id)}
                          title="Archivia immobile"
                          className="text-slate-600 hover:text-slate-900"
                          data-testid={`archive-imm-${imm.id}`}
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(imm.id)}
                        disabled={!canDelete}
                        className={!canDelete ? 'opacity-50 cursor-not-allowed' : ''}
                        title={!canDelete ? 'Impossibile eliminare: associato a contratti' : 'Elimina'}
                        data-testid={`delete-imm-${imm.id}`}
                      >
                        {canDelete ? <Trash2 className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Form - Create/Edit Immobile */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifica Immobile' : 'Nuovo Immobile'}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Ubicazione */}
              <div>
                <h3 className="font-semibold mb-3">Ubicazione</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Indirizzo *</Label>
                    <Input
                      value={formData.indirizzo}
                      onChange={(e) => setFormData({...formData, indirizzo: e.target.value})}
                      required
                      placeholder="Via, numero civico"
                    />
                  </div>
                  <div className="relative">
                    <Label>Città * (cerca comune)</Label>
                    <Input
                      value={formData.citta}
                      onChange={(e) => {
                        setFormData({...formData, citta: e.target.value});
                        searchComuni(e.target.value);
                      }}
                      placeholder="Cerca comune italiano..."
                      required
                    />
                    {comuniSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                        {comuniSuggestions.map((comune, idx) => (
                          <div 
                            key={`${comune.nome}-${idx}`} 
                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                            onClick={() => selectComune(comune)}
                          >
                            <span className="font-medium">{comune.nome}</span>
                            <span className="text-slate-500 ml-2">({comune.sigla_provincia})</span>
                            {comune.cap && (
                              <span className="text-slate-400 ml-2">
                                CAP: {comune.cap_list && comune.cap_list.length > 1 
                                  ? `${comune.cap_list[0]}-${comune.cap_list[comune.cap_list.length-1]}` 
                                  : comune.cap}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {searchingComuni && <p className="text-xs text-slate-500 mt-1">Ricerca in corso...</p>}
                  </div>
                  <div>
                    <Label>CAP *</Label>
                    <Input
                      value={formData.cap}
                      onChange={(e) => setFormData({...formData, cap: e.target.value})}
                      required
                      maxLength={5}
                    />
                  </div>
                  <div>
                    <Label>Provincia *</Label>
                    <Input
                      value={formData.provincia}
                      onChange={(e) => setFormData({...formData, provincia: e.target.value.toUpperCase()})}
                      required
                      maxLength={2}
                      placeholder="FI"
                    />
                  </div>
                </div>
              </div>

              {/* Proprietari - Only in creation mode */}
              {!editingItem && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold">Proprietari *</h3>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowNewProprietarioDialog(true)}
                      data-testid="new-proprietario-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Nuovo Proprietario
                    </Button>
                  </div>
                  <p className="text-sm text-slate-500 mb-3">
                    Seleziona uno o più proprietari per questo immobile
                  </p>
                  <ComboboxMulti
                    options={userOptions}
                    values={formData.proprietari_ids}
                    onValuesChange={(ids) => setFormData({...formData, proprietari_ids: ids})}
                    placeholder="Cerca e seleziona proprietari..."
                    searchPlaceholder="Cerca per nome..."
                    emptyText="Nessun utente trovato"
                  />
                  {formData.proprietari_ids.length > 0 && (
                    <p className="text-sm text-green-600 mt-2">
                      {formData.proprietari_ids.length} proprietario/i selezionato/i
                    </p>
                  )}
                </div>
              )}

              {/* Proprietari - Read-only in edit mode */}
              {editingItem && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Proprietari
                  </h3>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-slate-700">{getProprietariNomi(editingItem)}</p>
                    <p className="text-xs text-slate-500 mt-2">
                      Per modificare i proprietari, usa il pulsante apposito nella lista immobili
                    </p>
                  </div>
                </div>
              )}

              {/* Stato Immobile - Only in edit mode */}
              {editingItem && (
                <div>
                  <h3 className="font-semibold mb-3">Stato Immobile</h3>
                  <Select value={formData.stato} onValueChange={(v) => setFormData({...formData, stato: v})}>
                    <SelectTrigger data-testid="stato-immobile-select">
                      <SelectValue placeholder="Seleziona stato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="disponibile">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                          Disponibile
                        </div>
                      </SelectItem>
                      <SelectItem value="in_gestione">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-blue-600" />
                          In Gestione
                        </div>
                      </SelectItem>
                      <SelectItem value="archiviato">
                        <div className="flex items-center gap-2">
                          <Archive className="w-4 h-4 text-slate-500" />
                          Archiviato
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-2">
                    {formData.stato === 'disponibile' && "L'immobile può essere associato a un nuovo contratto"}
                    {formData.stato === 'in_gestione' && "L'immobile è attualmente associato a un contratto attivo"}
                    {formData.stato === 'archiviato' && "L'immobile non è più in gestione"}
                  </p>
                </div>
              )}

              {/* Dati Catastali */}
              <div>
                <h3 className="font-semibold mb-3">Dati Catastali</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>Codice Comune</Label>
                    <Input
                      value={formData.codice_comune}
                      onChange={(e) => setFormData({...formData, codice_comune: e.target.value.toUpperCase()})}
                      placeholder="D612"
                      maxLength={4}
                    />
                  </div>
                  <div>
                    <Label>Foglio</Label>
                    <Input
                      value={formData.foglio}
                      onChange={(e) => setFormData({...formData, foglio: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Particella</Label>
                    <Input
                      value={formData.particella}
                      onChange={(e) => setFormData({...formData, particella: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Subalterno</Label>
                    <Input
                      value={formData.subalterno}
                      onChange={(e) => setFormData({...formData, subalterno: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Categoria</Label>
                    <Input
                      value={formData.categoria}
                      onChange={(e) => setFormData({...formData, categoria: e.target.value.toUpperCase()})}
                      placeholder="A/2"
                    />
                  </div>
                  <div>
                    <Label>Classe</Label>
                    <Input
                      value={formData.classe}
                      onChange={(e) => setFormData({...formData, classe: e.target.value})}
                      placeholder="3"
                    />
                  </div>
                  <div>
                    <Label>Consistenza</Label>
                    <Input
                      value={formData.consistenza}
                      onChange={(e) => setFormData({...formData, consistenza: e.target.value})}
                      placeholder="5 vani"
                    />
                  </div>
                  <div>
                    <Label>Superficie Cat. (mq)</Label>
                    <Input
                      value={formData.superficie_catastale}
                      onChange={(e) => setFormData({...formData, superficie_catastale: e.target.value})}
                      placeholder="85"
                    />
                  </div>
                  <div>
                    <Label>Rendita (€)</Label>
                    <Input
                      value={formData.rendita}
                      onChange={(e) => setFormData({...formData, rendita: e.target.value})}
                      placeholder="650,00"
                    />
                  </div>
                </div>
              </div>

              {/* Note */}
              <div>
                <Label>Note</Label>
                <Input
                  value={formData.note}
                  onChange={(e) => setFormData({...formData, note: e.target.value})}
                  placeholder="Note aggiuntive..."
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Annulla
                </Button>
                <Button type="submit">
                  {editingItem ? 'Salva' : 'Crea'}
                </Button>
              </DialogFooter>
            </form>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Dialog - Edit Proprietari */}
      <Dialog open={showEditProprietariDialog} onOpenChange={setShowEditProprietariDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifica Proprietari</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-3">
              Seleziona i proprietari per: <strong>{editingItem?.indirizzo}</strong>
            </p>
            <ComboboxMulti
              options={userOptions}
              values={selectedProprietariIds}
              onValuesChange={setSelectedProprietariIds}
              placeholder="Cerca e seleziona proprietari..."
              searchPlaceholder="Cerca per nome..."
              emptyText="Nessun utente trovato"
            />
            {selectedProprietariIds.length > 0 && (
              <p className="text-sm text-green-600 mt-2">
                {selectedProprietariIds.length} proprietario/i selezionato/i
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditProprietariDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveProprietari}>
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Nuovo Proprietario Rapido */}
      <Dialog open={showNewProprietarioDialog} onOpenChange={setShowNewProprietarioDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Proprietario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Toggle Società */}
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <input
                type="checkbox"
                id="is_societa_prop"
                checked={newProprietarioData.is_societa}
                onChange={(e) => setNewProprietarioData({...newProprietarioData, is_societa: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="is_societa_prop" className="cursor-pointer">È una Società / Azienda</Label>
            </div>

            {newProprietarioData.is_societa ? (
              <div>
                <Label>Ragione Sociale *</Label>
                <Input 
                  value={newProprietarioData.ragione_sociale}
                  onChange={(e) => setNewProprietarioData({...newProprietarioData, ragione_sociale: e.target.value})}
                  placeholder="IMMOBILIARE ROSSI SRL"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome *</Label>
                  <Input 
                    value={newProprietarioData.nome}
                    onChange={(e) => setNewProprietarioData({...newProprietarioData, nome: e.target.value})}
                    placeholder="Mario"
                  />
                </div>
                <div>
                  <Label>Cognome *</Label>
                  <Input 
                    value={newProprietarioData.cognome}
                    onChange={(e) => setNewProprietarioData({...newProprietarioData, cognome: e.target.value})}
                    placeholder="Rossi"
                  />
                </div>
              </div>
            )}

            <div>
              <Label>Email</Label>
              <Input 
                type="email"
                value={newProprietarioData.email}
                onChange={(e) => setNewProprietarioData({...newProprietarioData, email: e.target.value})}
                placeholder="mario.rossi@email.com"
              />
            </div>

            <div>
              <Label>Codice Fiscale</Label>
              <Input 
                value={newProprietarioData.codice_fiscale}
                onChange={(e) => setNewProprietarioData({...newProprietarioData, codice_fiscale: e.target.value.toUpperCase()})}
                placeholder="RSSMRA80A01H501X"
                maxLength={16}
              />
            </div>

            <div>
              <Label>IBAN (per bonifici)</Label>
              <Input 
                value={newProprietarioData.iban}
                onChange={(e) => setNewProprietarioData({...newProprietarioData, iban: e.target.value.toUpperCase()})}
                placeholder="IT60X0542811101000000123456"
                maxLength={27}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewProprietarioDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreateProprietario}>
              Crea e Seleziona
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Documenti Immobile */}
      <Dialog open={showDocDialog} onOpenChange={setShowDocDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="doc-immobile-title">
              Documenti - {docImmobile?.indirizzo}, {docImmobile?.citta}
            </DialogTitle>
          </DialogHeader>

          {/* Upload form */}
          <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <Upload className="w-4 h-4" /> Carica Documento
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Tipo Documento *</Label>
                <Select value={docTipo} onValueChange={setDocTipo}>
                  <SelectTrigger data-testid="doc-tipo-select">
                    <SelectValue placeholder="Seleziona tipo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tipiDocImmobile.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">File *</Label>
                <Input
                  key={fileInputKey}
                  type="file"
                  onChange={(e) => setDocFile(e.target.files[0])}
                  className="text-sm"
                  data-testid="doc-file-input"
                />
              </div>
              <div>
                <Label className="text-xs">Note</Label>
                <Input
                  value={docNote}
                  onChange={(e) => setDocNote(e.target.value)}
                  placeholder="Note opzionali..."
                  data-testid="doc-note-input"
                />
              </div>
            </div>
            <Button
              onClick={handleUploadDocImmobile}
              disabled={uploadingDoc || !docFile || !docTipo}
              size="sm"
              data-testid="doc-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadingDoc ? 'Caricamento...' : 'Carica'}
            </Button>
          </div>

          {/* Lista documenti */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Documenti caricati ({documentiImmobile.length})</h4>
            {loadingDocs ? (
              <p className="text-sm text-slate-500">Caricamento...</p>
            ) : documentiImmobile.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Nessun documento caricato</p>
            ) : (
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-40">Tipo</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead className="w-28">Data</TableHead>
                    <TableHead className="text-right w-24">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documentiImmobile.map(doc => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <Badge variant="outline" className="text-xs whitespace-nowrap">
                          {tipiDocImmobile.find(t => t.value === doc.tipo_documento)?.label || doc.tipo_documento}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm max-w-xs truncate" title={doc.filename}>{doc.filename}</TableCell>
                      <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                        {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('it-IT') : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadDocImmobile(doc)} data-testid={`download-doc-${doc.id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteDocImmobile(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImmobiliPage;
